/**
 * canvas-engine/css/sanitizeCss.js
 *
 * OWN CSS sanitizer for the Enterprise Canvas Builder.
 *
 * ZERO REUSE: this module imports NOTHING from
 *   - admin-console-ui/src/modules/template-builder/
 *   - academy-ui/src/template/
 * and is deliberately NOT the existing `sanitizeCss` / `SectionCustomScope`.
 *
 * It is a pure, deterministic, dependency-free transform (no React, no DOM) so
 * it runs byte-identically in the Console Preview (builder) and the published
 * Reader. Same input always yields the same output.
 *
 * Responsibilities (Requirements 26.1, 26.2, 26.3; design Open Q3):
 *   - strip dangerous constructs: expression(), javascript:/vbscript: urls,
 *     behavior:, -moz-binding, @import, and script-like payloads
 *   - drop at-rules (selectors beginning with `@`)
 *   - drop unscoped rules (rules with no selector)
 *   - drop empty rules (no surviving declarations)
 *   - return sanitized CSS text
 *
 * The tokenizer is exported so the section scoper (scopeCustomCss.js) can reuse
 * the exact same parsing/sanitization pass and only differ in how it serializes
 * the selector (prefixing it with the section scope id).
 */

/**
 * Remove CSS comments (`/* ... *\/`). Done first so dangerous payloads cannot
 * hide inside comments and so comment text never affects rule parsing.
 * @param {string} css
 * @returns {string}
 */
function stripComments(css) {
  return css.replace(/\/\*[\s\S]*?\*\//g, '');
}

// Declaration-level constructs that must never reach the page.
// Matched case-insensitively against each individual declaration.
const DANGEROUS_DECLARATION_PATTERNS = [
  /expression\s*\(/i, // IE dynamic expressions
  /javascript\s*:/i, // javascript: urls
  /vbscript\s*:/i, // vbscript: urls
  /behavior\s*:/i, // IE behaviors / .htc binding
  /-moz-binding/i, // XBL binding
  /@import/i, // remote/local imports inside a declaration body
  /<\s*\/?\s*script/i, // script-like payloads
];

/**
 * Returns true when a single declaration ("prop: value") contains a construct
 * we refuse to emit.
 * @param {string} declaration
 * @returns {boolean}
 */
function isDangerousDeclaration(declaration) {
  return DANGEROUS_DECLARATION_PATTERNS.some((re) => re.test(declaration));
}

/**
 * Sanitize a rule body (the text between `{` and `}`): split into individual
 * declarations, drop any dangerous or malformed declaration, and re-serialize
 * deterministically. Returns '' when nothing safe survives.
 * @param {string} body
 * @returns {string}
 */
function sanitizeDeclarations(body) {
  if (!body) return '';
  const kept = [];
  const declarations = body.split(';');
  for (let i = 0; i < declarations.length; i += 1) {
    const declaration = declarations[i].trim();
    if (!declaration) continue; // empty fragment (e.g. trailing semicolon)
    if (declaration.indexOf(':') === -1) continue; // not a valid prop:value
    if (isDangerousDeclaration(declaration)) continue; // strip dangerous decl
    kept.push(declaration);
  }
  if (kept.length === 0) return '';
  return `${kept.join('; ')};`;
}

/**
 * Brace-depth-aware tokenizer. Walks the CSS character by character and splits
 * it into top-level rules of the form `{ selector, body }`. Nested braces in a
 * declaration body (rare, but possible) are preserved as part of the body so a
 * stray `}` cannot prematurely terminate a rule.
 *
 * A trailing selector with no opening brace is ignored.
 *
 * @param {string} css raw (comment-free) CSS text
 * @returns {Array<{selector: string, body: string}>}
 */
function tokenizeRules(css) {
  const rules = [];
  const n = css.length;
  let i = 0;
  let selector = '';

  while (i < n) {
    const ch = css[i];
    if (ch === '{') {
      // Consume a balanced body. depth starts at 1 for this opening brace.
      let depth = 1;
      let body = '';
      i += 1;
      while (i < n && depth > 0) {
        const c = css[i];
        if (c === '{') {
          depth += 1;
          body += c;
        } else if (c === '}') {
          depth -= 1;
          if (depth > 0) body += c; // keep inner closing braces, drop the outer
        } else {
          body += c;
        }
        i += 1;
      }
      rules.push({ selector: selector.trim(), body });
      selector = '';
    } else {
      selector += ch;
      i += 1;
    }
  }

  return rules;
}

/**
 * Parse CSS into the list of rules that are SAFE and SCOPABLE. This is the
 * shared pass used by both sanitizeCss (serializes selectors as-is) and
 * scopeCustomCss (prefixes selectors with the section scope id).
 *
 * Drops, in order:
 *   - at-rules        (selector begins with `@`, e.g. @import / @media / @font-face)
 *   - unscoped rules  (empty selector)
 *   - empty rules     (no surviving declarations after sanitization)
 *
 * @param {string} css
 * @returns {Array<{selector: string, body: string}>} sanitized, non-empty rules
 */
function parseSafeRules(css) {
  if (typeof css !== 'string' || css.length === 0) return [];
  const withoutComments = stripComments(css);
  const tokens = tokenizeRules(withoutComments);
  const safe = [];

  for (let i = 0; i < tokens.length; i += 1) {
    const { selector, body } = tokens[i];
    if (!selector) continue; // unscoped rule
    if (selector.charAt(0) === '@') continue; // at-rule
    const safeBody = sanitizeDeclarations(body);
    if (!safeBody) continue; // empty rule
    safe.push({ selector, body: safeBody });
  }

  return safe;
}

/**
 * Public sanitizer. Returns sanitized CSS text with all dangerous constructs,
 * at-rules, unscoped rules, and empty rules removed. Deterministic.
 *
 * @param {string} css author-provided CSS
 * @returns {string} sanitized CSS text ('' when nothing safe survives)
 */
function sanitizeCss(css) {
  const rules = parseSafeRules(css);
  if (rules.length === 0) return '';
  return rules.map((rule) => `${rule.selector} { ${rule.body} }`).join('\n');
}

module.exports = {
  sanitizeCss,
  // Exported for reuse by scopeCustomCss.js (same canvas-engine module).
  parseSafeRules,
  // Exported for unit/property testing of the lower-level pieces.
  tokenizeRules,
  sanitizeDeclarations,
  stripComments,
};
